import { Component, Element, h, Host, Listen, Prop } from '@stencil/core';
import isChromatic from 'chromatic/isChromatic';
import { Breakpoint, getCurrentBreakpoint } from '../_helpers/breakpoints';
import {
	GroupByOption,
	TableHighlightColor,
	TableHighlightMode,
	TableLayoutBreakpointRangeCache,
	TableLayoutMode,
	UseTableViewCriteria,
} from '../_types/table';

const WindowResizeDebounceDelay = 200;

const OneLineTextHeight = 20;
const TwoLinesTextHeight = 44;
const ThreeLinesTextHeight = 68;

@Component({
	tag: 'ds-table',
	shadow: false,
})
export class Table {
	@Prop({ reflect: true }) highlighting: TableHighlightMode;
	@Prop({ reflect: true, mutable: true }) highlightColor: TableHighlightColor =
		TableHighlightColor.White;
	@Prop({ mutable: true }) useTableViewWhen: UseTableViewCriteria;
	@Prop() groupBy: GroupByOption;
	layoutMode: TableLayoutMode = TableLayoutMode.Table;

	@Element() el: HTMLDsTableElement;

	tableEl: HTMLTableElement; // Single <table> element
	tableCaption: HTMLTableCaptionElement; // Single <caption> element
	tableHeaders: HTMLTableHeaderCellElement[]; // Array of all <th> elements in table
	tableData: HTMLTableRowElement[] = []; // Array of all <tr> elements in table
	tableCells: HTMLTableCellElement[] = []; // Array of all <th> and <td> elements in table

	setLayoutTimer: number;
	cachedBreakpointWidthRange: TableLayoutBreakpointRangeCache = {
		knownRangeMaximum: null,
		knownRangeMinimum: null,
	};

	/**
	 * Initial setup actions for the component on page load
	 */
	componentWillLoad(): void {
		const tables = Array.from(
			this.el.querySelectorAll<HTMLTableElement>('table')
		);

		if (tables.length !== 1) {
			console.error('Exactly one HTML table must exist inside a <ds-table>');
			return;
		}

		this.tableEl = tables[0];
		this.tableCaption = this.tableEl.querySelector('caption');

		this.tableData = Array.from(this.tableEl.querySelectorAll('tbody tr'));
		this.tableHeaders = Array.from(this.tableEl.querySelectorAll('th'));
		this.tableCells = Array.from(this.tableEl.querySelectorAll('th, td'));

		this.wrapTableCellContent();

		this.calculateLayoutMode(window.innerWidth);
	}

	/**
	 * Wrap the text content of table <th> and <td> elements with a span - this allows a calculation of the height occupied
	 * by the text that doesn't include the padding.
	 * !@see calculateCellsExceedHeight
	 */
	wrapTableCellContent(): void {
		this.tableCells.forEach((td) => {
			td.innerHTML = `<span>${td.innerHTML}</span>`;
		});
	}

	/**
	 * Listen to the window being resized and check the cache for the mode for that window width.
	 * If it hasn't been calculated yet for that width, then after a 'debounce' period, start the calculation.
	 */
	@Listen('resize', { target: 'window' })
	async windowResizeHandler(): Promise<void> {
		const modeFromCache: TableLayoutMode = this.calculateModeFromCache(
			window.innerWidth
		);

		if (modeFromCache) {
			this.applyLayoutMode(modeFromCache);
			return;
		}

		if (this.setLayoutTimer) {
			window.clearTimeout(this.setLayoutTimer);
		}

		if (!isChromatic()) {
			this.setLayoutTimer = window.setTimeout(
				() => this.calculateLayoutMode(window.innerWidth),
				WindowResizeDebounceDelay
			);
		} else {
			this.calculateLayoutMode(window.innerWidth);
		}
	}

	/**
	 * Calculate and set the layout mode to 'table' or 'cards'.
	 * The calculation depends how the table has been configured. It may look at the current breakpoint or the space
	 * available for text inside the table.
	 *
	 * @param windowWidth  The current window width, used to fetch the current breakpoint which is sometimes used to
	 *                     calculate the layout mode
	 */
	calculateLayoutMode = (windowWidth: number): void => {
		const currentBreakpoint = getCurrentBreakpoint(windowWidth);

		switch (this.useTableViewWhen) {
			case UseTableViewCriteria.Always:
				this.applyLayoutMode(TableLayoutMode.Table);
				break;
			case UseTableViewCriteria.Never:
				this.applyLayoutMode(TableLayoutMode.Cards);
				break;
			case UseTableViewCriteria.FromBreakpointSm:
				if (currentBreakpoint === Breakpoint.XS) {
					this.applyLayoutMode(TableLayoutMode.Cards);
				} else {
					this.applyLayoutMode(TableLayoutMode.Table);
				}
				break;
			case UseTableViewCriteria.FromBreakpointMd:
				if (
					([Breakpoint.XS, Breakpoint.SM] as Breakpoint[]).includes(
						currentBreakpoint
					)
				) {
					this.applyLayoutMode(TableLayoutMode.Cards);
				} else {
					this.applyLayoutMode(TableLayoutMode.Table);
				}
				break;
			case UseTableViewCriteria.FromBreakpointLg:
				if (
					(
						[Breakpoint.XS, Breakpoint.SM, Breakpoint.MD] as Breakpoint[]
					).includes(currentBreakpoint)
				) {
					this.applyLayoutMode(TableLayoutMode.Cards);
				} else {
					this.applyLayoutMode(TableLayoutMode.Table);
				}
				break;
			case UseTableViewCriteria.FromBreakpointXl:
				if (currentBreakpoint === Breakpoint.XL) {
					this.applyLayoutMode(TableLayoutMode.Table);
				} else {
					this.applyLayoutMode(TableLayoutMode.Cards);
				}
				break;

			case UseTableViewCriteria.HeadingsFitOnOneLine:
			case UseTableViewCriteria.HeadingsFitOnTwoLines:
			case UseTableViewCriteria.HeadingsFitOnThreeLines:
			case UseTableViewCriteria.AllCellsFitOnOneLine:
			case UseTableViewCriteria.AllCellsFitOnTwoLines:
			case UseTableViewCriteria.AllCellsFitOnThreeLines:
				this.applyLayoutMode(this.calculateLayoutModeByCellSize(windowWidth));
				break;
		}
	};

	/**
	 * Apply the correct layout mode to the component by editing which CSS classes are present.
	 *
	 * Note: It would be better to use @State instead of directly editing the element's classList but there is a bug in Stencil
	 * that affects nested tooltips when we do this. Editing the classList instead is the only easy workaround to avoid
	 * this bug: https://github.com/ionic-team/stencil/issues/1948
	 * Upgrading Stencil to 1.17.3 didn't fix it, and neither did editing the config with 'cloneNodeFix: true'
	 *
	 * @param layoutMode
	 */
	applyLayoutMode(layoutMode: TableLayoutMode) {
		this.layoutMode = layoutMode;
		this.el.classList.remove(`ds-mode-${TableLayoutMode.Cards}`);
		this.el.classList.remove(`ds-mode-${TableLayoutMode.Table}`);
		this.el.classList.add(`ds-mode-${layoutMode}`);
	}

	/**
	 * Determine which layout mode to use by looking at how easily text fits inside cells (either heading <th> or normal
	 * <td> cells)
	 * Depending on the 'useTableViewWhen' prop value, it will check how many lines are occupied and if that's within the
	 * allowed number, it will return 'table' mode, otherwise if it's too squashed, it will return 'cards' mode.
	 */
	calculateLayoutModeByCellSize(windowWidth: number): TableLayoutMode {
		let contentFits;

		switch (this.useTableViewWhen) {
			case UseTableViewCriteria.HeadingsFitOnOneLine:
				contentFits = !this.calculateCellsExceedHeight(
					OneLineTextHeight,
					this.tableHeaders
				);
				break;

			case UseTableViewCriteria.HeadingsFitOnTwoLines:
				contentFits = !this.calculateCellsExceedHeight(
					TwoLinesTextHeight,
					this.tableHeaders
				);
				break;

			case UseTableViewCriteria.HeadingsFitOnThreeLines:
				contentFits = !this.calculateCellsExceedHeight(
					ThreeLinesTextHeight,
					this.tableHeaders
				);
				break;

			case UseTableViewCriteria.AllCellsFitOnOneLine:
				contentFits = !this.calculateCellsExceedHeight(
					OneLineTextHeight,
					this.tableCells
				);
				break;

			case UseTableViewCriteria.AllCellsFitOnTwoLines:
				contentFits = !this.calculateCellsExceedHeight(
					TwoLinesTextHeight,
					this.tableCells
				);
				break;

			case UseTableViewCriteria.AllCellsFitOnThreeLines:
				contentFits = !this.calculateCellsExceedHeight(
					ThreeLinesTextHeight,
					this.tableCells
				);
				break;

			default:
				return null;
		}

		const newMode = contentFits ? TableLayoutMode.Table : TableLayoutMode.Cards;
		this.updateCache(windowWidth, newMode);

		return newMode;
	}

	/**
	 * Attempt to work out the mode by looking only at the cache. If this is not possible then this function will return
	 * null so that it can be calculated another way.
	 *
	 * @return TableLayoutMode|null
	 */
	calculateModeFromCache(windowWidth: number): TableLayoutMode {
		if (this.cachedBreakpointWidthRange.knownRangeMinimum) {
			if (windowWidth < this.cachedBreakpointWidthRange.knownRangeMinimum) {
				return TableLayoutMode.Cards;
			}
		}

		if (this.cachedBreakpointWidthRange.knownRangeMaximum) {
			if (windowWidth >= this.cachedBreakpointWidthRange.knownRangeMaximum) {
				return TableLayoutMode.Table;
			}
		}

		return null;
	}

	/**
	 * Update or initialise the cache, which is a range of two numbers that can change and become more accurate as more
	 * calculations are made.
	 *
	 * - If the current window width is above the maximum, then the mode should be Table.
	 * - If the current window width is below the minimum, then the mode should be Cards.
	 * - If the current window width is between the two numbers, then we have to recalculate.
	 */
	updateCache(windowWidth: number, newMode: TableLayoutMode): void {
		if (newMode === TableLayoutMode.Cards) {
			this.updateCacheMinValue(windowWidth);
		}
		if (newMode === TableLayoutMode.Table) {
			this.updateCacheMaxValue(windowWidth);
		}
	}

	/**
	 * Update the cache range's minimum value, if it is more precise than it already knows about, or if it is as yet unset
	 */
	updateCacheMinValue(windowWidth: number) {
		if (this.cachedBreakpointWidthRange.knownRangeMinimum) {
			if (windowWidth > this.cachedBreakpointWidthRange.knownRangeMinimum) {
				this.cachedBreakpointWidthRange.knownRangeMinimum = windowWidth;
			}
		} else {
			this.cachedBreakpointWidthRange.knownRangeMinimum = windowWidth;
		}
	}

	/**
	 * Update the cache range's maximum value, if it is more precise than it already knows about, or if it is as yet unset
	 */
	updateCacheMaxValue(windowWidth: number) {
		if (this.cachedBreakpointWidthRange.knownRangeMaximum) {
			if (windowWidth < this.cachedBreakpointWidthRange.knownRangeMaximum) {
				this.cachedBreakpointWidthRange.knownRangeMaximum = windowWidth;
			}
		} else {
			this.cachedBreakpointWidthRange.knownRangeMaximum = windowWidth;
		}
	}

	calculateCellsExceedHeight = (
		maxHeight: number,
		cells: HTMLElement[]
	): boolean => {
		let textHeightExceedsMaximum = false;
		cells.forEach((td) => {
			const textHeight = td.querySelector('span').offsetHeight;

			if (textHeight > maxHeight) {
				textHeightExceedsMaximum = true;
			}
		});

		return textHeightExceedsMaximum;
	};

	renderCaption() {
		if (this.tableCaption) {
			return (
				<div
					class="ds-cards-caption"
					innerHTML={this.tableCaption.innerHTML}
				></div>
			);
		}
	}

	/**
	 * Render a row into a card. This is for grouping by None or by Row
	 * @param tr
	 */
	renderRowCard(tr: HTMLTableRowElement) {
		const cellsInRow = Array.from(tr.querySelectorAll('td'));

		return (
			<table class="ds-table-row-card">
				<tbody>
					{/* Card Heading */}
					{this.groupBy === GroupByOption.RowHeader ? (
						<tr
							innerHTML={`<td class="ds-card-heading ds-heading-xs-lc" colspan="2">${
								tr.querySelector('td:first-child').innerHTML
							}</td>`}
						/>
					) : null}

					{/* Card Body Items */}
					{cellsInRow.map((td: HTMLTableCellElement, i) => {
						const index = td.cellIndex || i; // td.cellIndex for in browser, or i for when unit testing and cellIndex isn't available

						// Exclude first field if it was used as a header already
						// Otherwise render this field as an item in the card
						return this.groupBy === GroupByOption.RowHeader &&
							i === 0 ? null : (
							<tr class="ds-item">
								<td
									class="ds-item-heading ds-table-cell"
									innerHTML={this.tableHeaders[index].innerHTML}
								/>
								<td
									class={`ds-table-cell ${td.className}`}
									innerHTML={td.innerHTML}
								/>
							</tr>
						);
					})}
				</tbody>
			</table>
		);
	}

	/**
	 * Render the whole table into cards that are based on a column
	 * @param th
	 */
	renderCardsByColumn(th: HTMLTableCellElement) {
		const numColumnCells = this.tableHeaders.length;

		// skip for first column header cell
		return th.cellIndex === 0 ? null : (
			<table class="ds-table-row-card">
				<tbody>
					{/* Card Heading */}
					<tr
						innerHTML={`<td class="ds-card-heading ds-heading-xs-lc" colspan="2">${th.innerHTML}</td>`}
					/>

					{/* Card Body Items */}
					{this.tableCells.map((td: HTMLTableCellElement, i) => {
						// we want skip the first cell and limit the rows of each card to same number of rows of original table
						// here td will be the first cell of each row in the original table
						// adding col Index will guarantee that the cell was under this column in the original table
						return i > 0 && i % numColumnCells == 0 ? (
							<tr class="ds-item">
								<td
									class="ds-item-heading ds-table-cell"
									innerHTML={td.innerHTML}
								/>
								<td
									class={`ds-table-cell ${
										this.tableCells[i + th.cellIndex].className
									}`}
									innerHTML={this.tableCells[i + th.cellIndex].innerHTML}
								/>
							</tr>
						) : null;
					})}
				</tbody>
			</table>
		);
	}

	getClasses(): { [key: string]: boolean } {
		const classes = {};
		classes[`ds-mode-${this.layoutMode}`] = true;

		return classes;
	}

	render() {
		return (
			<Host class={this.getClasses()}>
				{/* Card Layout */}
				<section class="ds-cards-container">
					{this.renderCaption()}
					{this.groupBy === GroupByOption.ColumnHeader
						? this.tableHeaders.map((th) => this.renderCardsByColumn(th))
						: this.tableData.map((tr) => this.renderRowCard(tr))}
				</section>

				{/* Table Layout */}
				<slot />
			</Host>
		);
	}
}
